
import { createContext, useContext, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Workout, Exercise, SetDetail } from "@/data/dummyData";
import { completeAssignedWorkout } from "@/integrations/backend/workout-assigned";

type WorkoutContextType = {
  isWorkoutActive: boolean;
  activeWorkout: Workout | null;
  activeAssignedWorkoutId: string | null;
  isPaused: boolean;
  completedExercises: string[];
  activeExerciseIndex: number;
  showPostWorkoutFeedback: boolean;
  setWorkoutActive: (active: boolean) => void;
  startWorkout: (workout: Workout) => void;
  replaceActiveWorkout: (workout: Workout) => void;
  setActiveAssignedWorkoutId: (id: string | null) => void;
  pauseResumeWorkout: () => void;
  endWorkout: (elapsedSeconds?: number) => Promise<void>;
  endWorkoutImmediately: () => void;
  showPostWorkoutFeedbackDialog: () => void;
  hidePostWorkoutFeedbackDialog: () => void;
  submitPostWorkoutFeedback: (rpe: number, comments: string) => Promise<void>;
  returnToWorkout: () => void;
  completeExercise: (exerciseId: string) => void;
  setActiveExerciseIndex: (index: number) => void;
};

const WorkoutContext = createContext<WorkoutContextType | undefined>(undefined);

export const WorkoutProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [isWorkoutActive, setIsWorkoutActive] = useState(false);
  const [activeWorkout, setActiveWorkout] = useState<Workout | null>(null);
  const [activeAssignedWorkoutId, setActiveAssignedWorkoutId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [completedExercises, setCompletedExercises] = useState<string[]>([]);
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
  const [showPostWorkoutFeedback, setShowPostWorkoutFeedback] = useState(false);
  const [elapsedTimeSeconds, setElapsedTimeSeconds] = useState(0);

  const setWorkoutActive = (active: boolean) => {
    setIsWorkoutActive(active);
  };

  const startWorkout = (workout: Workout) => {
    setActiveWorkout(workout);
    setIsWorkoutActive(true);
    setIsPaused(false);
    setCompletedExercises([]);
    setActiveExerciseIndex(0);
  };

  const replaceActiveWorkout = (workout: Workout) => {
    setActiveWorkout(workout);
  };

  const pauseResumeWorkout = () => {
    setIsPaused(!isPaused);
  };

  const endWorkout = async (elapsedSeconds?: number) => {
    // Get elapsed time from cookie if not provided
    if (elapsedSeconds === undefined) {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('workout_elapsed_time='))
        ?.split('=')[1];
      if (cookieValue) {
        const savedSeconds = parseInt(cookieValue, 10);
        if (!isNaN(savedSeconds)) {
          elapsedSeconds = savedSeconds;
        }
      }
    }
    const finalElapsedSeconds = elapsedSeconds || 0;
    setElapsedTimeSeconds(finalElapsedSeconds);
    
    // Save CompletedWorkout with default values before showing popup
    if (activeAssignedWorkoutId) {
      try {
        const elapsedMinutes = Math.round(finalElapsedSeconds / 60);
        await completeAssignedWorkout(activeAssignedWorkoutId, {
          // Default values - feedback will be updated later if user submits feedback
          rpe: null,
          feedback_comments: null,
          intensity: 0,
          energy: 0,
          points: 0,
          minutes: elapsedMinutes,
          status: "R",
        });
        console.log("CompletedWorkout saved with default values");
      } catch (error) {
        console.error("Error saving CompletedWorkout:", error);
        // Continue to show popup even if save fails
      }
    }
    
    // Show post-workout feedback dialog
    setShowPostWorkoutFeedback(true);
  };

  const endWorkoutImmediately = () => {
    try {
      console.log("Ending workout immediately");
      setIsWorkoutActive(false);
      setActiveWorkout(null);
      setActiveAssignedWorkoutId(null);
      setIsPaused(false);
      setCompletedExercises([]);
      setActiveExerciseIndex(0);
      setShowPostWorkoutFeedback(false);
      navigate("/client-portal");
    } catch (error) {
      console.error("Error ending workout immediately:", error);
      // Force navigation even if state update fails
      navigate("/client-portal");
    }
  };

  const showPostWorkoutFeedbackDialog = () => {
    setShowPostWorkoutFeedback(true);
  };

  const hidePostWorkoutFeedbackDialog = () => {
    // Close popup and redirect to client portal
    setShowPostWorkoutFeedback(false);
    
    // Clear workout state
    setIsWorkoutActive(false);
    setActiveWorkout(null);
    setActiveAssignedWorkoutId(null);
    setIsPaused(false);
    setCompletedExercises([]);
    setActiveExerciseIndex(0);
    setElapsedTimeSeconds(0);
    
    // Clear cookie
    document.cookie = "workout_elapsed_time=; path=/; max-age=0";
    
    // Navigate to client portal
    navigate("/client-portal");
  };

  const submitPostWorkoutFeedback = async (rpe: number, comments: string) => {
    try {
      // Convert elapsed seconds to minutes (rounded)
      const elapsedMinutes = Math.round(elapsedTimeSeconds / 60);
      console.log("Starting submitPostWorkoutFeedback with:", { rpe, comments, activeAssignedWorkoutId, elapsedMinutes });
      
      // Update existing CompletedWorkout with feedback data
      if (activeAssignedWorkoutId) {
        await completeAssignedWorkout(activeAssignedWorkoutId, {
          rpe,
          feedback_comments: comments,
          // Intensity = how hard it felt (RPE 1-10)
          // Points = intensity * minutes
          intensity: rpe,
          energy: 0,
          points: rpe * elapsedMinutes,
          minutes: elapsedMinutes,
          status: "R",
        });
      }

      // Persist workout load entry locally for charting on the home tab
      try {
        const existingRaw = localStorage.getItem("workoutLoad");
        const existing: Array<{ timestamp: string; difficulty: number; workoutTitle?: string | null; comments?: string | null; }> =
          existingRaw ? JSON.parse(existingRaw) : [];
        existing.push({
          timestamp: new Date().toISOString(),
          difficulty: rpe,
          workoutTitle: activeWorkout?.title ?? null,
          comments: comments || null
        });
        localStorage.setItem("workoutLoad", JSON.stringify(existing));
      } catch (persistError) {
        console.error("Failed to persist workout load entry:", persistError);
      }
      
      // End the workout after feedback is submitted
      console.log("Clearing workout state...");
      setIsWorkoutActive(false);
      setActiveWorkout(null);
      setActiveAssignedWorkoutId(null);
      setIsPaused(false);
      setCompletedExercises([]);
      setActiveExerciseIndex(0);
      setShowPostWorkoutFeedback(false);
      setElapsedTimeSeconds(0);
      
      // Clear cookie
      document.cookie = "workout_elapsed_time=; path=/; max-age=0";
      
      // Navigate to the home tab (client-portal) immediately
      console.log("Navigating to /client-portal");
      navigate("/client-portal");
      console.log("Navigation called");
    } catch (error) {
      console.error("Error submitting post-workout feedback:", error);
      // Even if feedback submission fails, still end the workout
      console.log("Error occurred, still ending workout...");
      setIsWorkoutActive(false);
      setActiveWorkout(null);
      setActiveAssignedWorkoutId(null);
      setIsPaused(false);
      setCompletedExercises([]);
      setActiveExerciseIndex(0);
      setShowPostWorkoutFeedback(false);
      setElapsedTimeSeconds(0);
      document.cookie = "workout_elapsed_time=; path=/; max-age=0";
      navigate("/client-portal");
    }
  };

  const returnToWorkout = () => {
    // Navigate back to workout session
    navigate("/workout-session");
  };

  const completeExercise = (exerciseId: string) => {
    if (!completedExercises.includes(exerciseId)) {
      setCompletedExercises([...completedExercises, exerciseId]);
    } else {
      // Toggle completion
      setCompletedExercises(completedExercises.filter(id => id !== exerciseId));
    }
  };

  return (
    <WorkoutContext.Provider 
      value={{ 
        isWorkoutActive,
        activeWorkout,
        activeAssignedWorkoutId,
        isPaused,
        completedExercises,
        activeExerciseIndex,
        showPostWorkoutFeedback,
        setWorkoutActive,
        startWorkout,
        replaceActiveWorkout,
        setActiveAssignedWorkoutId,
        pauseResumeWorkout,
        endWorkout,
        endWorkoutImmediately,
        showPostWorkoutFeedbackDialog,
        hidePostWorkoutFeedbackDialog,
        submitPostWorkoutFeedback,
        returnToWorkout,
        completeExercise,
        setActiveExerciseIndex
      }}
    >
      {children}
    </WorkoutContext.Provider>
  );
};

export const useWorkout = () => {
  const context = useContext(WorkoutContext);
  if (context === undefined) {
    throw new Error("useWorkout must be used within a WorkoutProvider");
  }
  return context;
};
